import type { TariffsState } from './Quote.types';
import { defineStore } from 'pinia';
import { computed, onBeforeMount, reactive } from 'vue';
import { useStateUpdate } from '@/shared/lib/pinia/useStateUpdate';
import { getCookieByName, setCookie } from '@/shared/utils/cookie';
import { QUOTES_CACHE_KEY } from '../lib/Quote.constants';
import { extractPricePropsFromTariff, selectMinPriceTariff } from '../lib/Quote.utils';

const storeKey = 'TariffsStore';

function defaultState(): TariffsState {
  return {
    quotes: null,
    quoteError: null,
  };
}

export const useTariffsStore = defineStore(storeKey, () => {
  const state = reactive<TariffsState>(defaultState());
  const $update = useStateUpdate<TariffsState>(state);

  const reset = () => {
    Object.assign(state, defaultState());
  };

  const invalidateQuotesCache = (force = false) => {
    let quotesCacheIsValid: boolean;
    if (force) {
      setCookie(QUOTES_CACHE_KEY, -1, { daysToLive: -1 });
      state.quotes = null;
      return;
    } else {
      quotesCacheIsValid = !!getCookieByName(QUOTES_CACHE_KEY);
    }

    // TODO: check if this is still needed
    // const rejectedQuotes
    //   = state.quotes?.tpl === null
    //     || state.quotes?.comprehensive === null;
    //
    // const hasEmptyTariffs
    //   = !state.quotes?.comprehensive?.tariffs
    //     || state.quotes?.tpl?.tariffs.length === 0
    //     || state.quotes?.comprehensive?.tariffs.length === 0;
    //
    // if (rejectedQuotes || hasEmptyTariffs) {
    //   quotesCacheIsValid = false;
    // }

    if (!quotesCacheIsValid) {
      state.quotes = null;
      setCookie(QUOTES_CACHE_KEY, -1, { daysToLive: -1 });
    }
  };

  const tpl = computed(() => state.quotes?.tpl ?? null);
  const tplTariff = computed(() => tpl.value?.tariffs[0]);

  const tplPrices = computed(() => {
    if (!tpl.value) return null;
    return extractPricePropsFromTariff(tpl.value.tariffs);
  });

  const isTplOffline = computed(() => !tplPrices);

  const comprehensive = computed(() => state.quotes?.comprehensive ?? null);
  const comprehensiveTariffs = computed(() => comprehensive.value?.tariffs ?? []);

  const cheapestComprehensiveTariff = computed(() => selectMinPriceTariff(comprehensive.value?.tariffs ?? []));

  const comprehensivePrices = computed(() => {
    if (!comprehensive.value) return null;
    return extractPricePropsFromTariff(comprehensive.value.tariffs);
  });

  const isComprehensiveOffline = computed(() => !comprehensivePrices);
  const isOnlyComprehensiveAvailable = computed(() => Boolean(state.quotes?.comprehensive && !state.quotes?.tpl));
  const isOnlyTPLAvailable = computed(() => Boolean(state.quotes?.tpl && !state.quotes?.comprehensive));

  const hasValidQuotesCache = computed(() => {
    return state.quotes !== null;
  });

  onBeforeMount(invalidateQuotesCache);

  return {
    state,
    $update,
    reset,
    tpl,
    tplTariff,
    tplPrices,
    isTplOffline,
    comprehensive,
    comprehensiveTariffs,
    comprehensiveTariff: cheapestComprehensiveTariff,
    cheapestComprehensiveTariff,
    comprehensivePrices,
    isComprehensiveOffline,
    isOnlyTPLAvailable,
    isOnlyComprehensiveAvailable,
    hasValidQuotesCache,
    invalidateQuotesCache,
  };
}, { persist: { key: storeKey } });
